import { useCallback } from 'react';
import { useSessionNotes, useCreateNote, useUpdateNote, useDeleteNote, generateNoteId } from '../utils/notesQueries';
import { YoutubeVod } from '../components/Notes';
import { AuthState } from '../utils/auth';

interface UseReactQueryNotesProps {
  youtube_vods: YoutubeVod[];
  active_youtube_id: string;
  authState: AuthState;
}

export const useReactQueryNotes = ({ youtube_vods, active_youtube_id, authState }: UseReactQueryNotesProps) => {
  // Only fetch notes if user has PRO subscription and auth is complete
  const enabled = authState.progress === 'DONE' && authState.userStatus === 'PRO';
  
  const { sessionNotes, isLoading, isError, error } = useSessionNotes(youtube_vods, enabled);
  
  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote();
  const deleteNoteMutation = useDeleteNote();

  // Treat incoming time as VOD-relative time (seconds_from_start)
  const createNote = useCallback(async (content: string, vod_time: number, labels: Record<string, string> = {}, noteId?: string) => {
    const seconds_from_start = Math.max(0, vod_time);
    const finalNoteId = noteId || generateNoteId();
    
    return new Promise<string>((resolve, reject) => {
      createNoteMutation.mutate({
        youtubeId: active_youtube_id,
        noteId: finalNoteId,
        content,
        seconds_from_start,
        labels
      }, {
        onSuccess: () => {
          resolve(finalNoteId);
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [active_youtube_id, createNoteMutation]);

  const updateNote = useCallback(async (note_id: string, content: string, vod_time: number, labels: Record<string, string> = {}) => {
    const seconds_from_start = Math.max(0, vod_time);
    
    // Find the current note to get its version
    const currentNote = sessionNotes.find(note => note.note_id === note_id);
    if (!currentNote) {
      throw new Error('Note not found');
    }
    
    return new Promise<void>((resolve, reject) => {
      updateNoteMutation.mutate({
        noteId: note_id,
        content,
        seconds_from_start,
        labels,
        version: currentNote.version
      }, {
        onSuccess: () => {
          resolve();
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [sessionNotes, updateNoteMutation]);

  const deleteNote = useCallback(async (note_id: string) => {
    return new Promise<void>((resolve, reject) => {
      deleteNoteMutation.mutate(note_id, {
        onSuccess: () => {
          resolve();
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [deleteNoteMutation]);

  const addLabelToNote = useCallback(async (noteId: string, labelName: string, labelColor: string) => {
    // Find the current note
    const currentNote = sessionNotes.find(note => note.note_id === noteId);
    if (!currentNote) {
      throw new Error('Note not found');
    }

    // Update the note with the new label
    const updatedLabels = { ...currentNote.labels, [labelName]: labelColor };
    
    return new Promise<void>((resolve, reject) => {
      updateNoteMutation.mutate({
        noteId,
        content: currentNote.content,
        seconds_from_start: currentNote.seconds_from_start,
        labels: updatedLabels,
        version: currentNote.version
      }, {
        onSuccess: () => {
          resolve();
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [sessionNotes, updateNoteMutation]);

  const removeLabelFromNote = useCallback(async (noteId: string, labelName: string) => {
    // Find the current note
    const currentNote = sessionNotes.find(note => note.note_id === noteId);
    if (!currentNote) {
      throw new Error('Note not found');
    }

    // Update the note without the specified label
    const updatedLabels = { ...currentNote.labels };
    delete updatedLabels[labelName];
    
    return new Promise<void>((resolve, reject) => {
      updateNoteMutation.mutate({
        noteId,
        content: currentNote.content,
        seconds_from_start: currentNote.seconds_from_start,
        labels: updatedLabels,
        version: currentNote.version
      }, {
        onSuccess: () => {
          resolve();
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [sessionNotes, updateNoteMutation]);

  // Format error message for UI
  const formatError = (error: any): string => {
    if (error?.response?.status === 409) {
      return 'This note has been modified by another user. Please refresh to see the latest version.';
    }
    if (error?.message) {
      return error.message;
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const errorMessage = isError ? formatError(error) : null;

  return {
    sessionNotes,
    loading: isLoading,
    error: errorMessage,
    createNote,
    updateNote,
    deleteNote,
    addLabelToNote,
    removeLabelFromNote,
    // Expose mutation states for additional error handling
    isCreating: createNoteMutation.isPending,
    isUpdating: updateNoteMutation.isPending,
    isDeleting: deleteNoteMutation.isPending,
    createError: createNoteMutation.error ? formatError(createNoteMutation.error) : null,
    updateError: updateNoteMutation.error ? formatError(updateNoteMutation.error) : null,
    deleteError: deleteNoteMutation.error ? formatError(deleteNoteMutation.error) : null,
  };
}; 